import { useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { LoginFormData, AuthError } from '../types/auth';
import { authChallenge } from '../../../redux/actions/authActions';
import axios from 'axios';
import { STAGE } from '../../../config';
import { userSignIn, userSignOut } from '../../../services/auth';
import { finalizeLogin } from '../utils/loginFinalize';
import { logUserLoginFailedAudit } from '../utils/authAudit';

const { REACT_APP_EMAIL_NOTIFICATION_BASE_URL } = process.env;

/**
 * Send OTP to user's email for login
 */
async function sendLoginOtp(data: { email: string }) {
  try {
    const res = await axios.post(`${REACT_APP_EMAIL_NOTIFICATION_BASE_URL}/auth/send-otp`, data);
    console.log("Login OTP sent successfully:", res.data);
    return res.data;
  } catch (err: any) {
    console.error("Error sending login OTP:", err.response?.data || err.message);
    throw err.response?.data || { message: "Failed to send OTP" };
  }
}

/**
 * Verify OTP for login
 */
async function verifyLoginOtp(data: { email: string; code: string }) {
  try {
    const res = await axios.post(
      `${REACT_APP_EMAIL_NOTIFICATION_BASE_URL}/auth/verify-otp`,
      data
    );
    console.log("Login OTP verified successfully:", res.data);
    return res.data;
  } catch (err: any) {
    console.error("Error verifying login OTP:", err.response?.data || err.message);
    throw err.response?.data || { message: "Failed to verify OTP" };
  }
}

interface UseReduxLoginFormReturn {
  formData: LoginFormData;
  isLoading: boolean;
  error: AuthError | null;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSubmit: (e: React.FormEvent) => Promise<void>;
  otpSent: boolean;
  otp: string;
  setOtp: (otp: string) => void;
  otpVerified: boolean;
  setOtpVerified: (verified: boolean) => void;
  handleSendOtp: () => Promise<boolean>;
  handleVerifyOtp: () => Promise<void>;
  isVerifying: boolean;
  timer: number;
  canResend: boolean;
  handleResendOtp: () => Promise<void>;
  handleCancelOtp: () => void;
  resendMessage: string | null;
}

const INITIAL_FORM_STATE: LoginFormData = {
  email: '',
  password: '',
  rememberMe: false
};

const REMEMBERED_LOGIN_KEY = 'data360_remembered_login';
const REMEMBERED_LOGIN_SECRET = 'data360-login-remember';

const bytesToBase64 = (bytes: Uint8Array) => {
  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return window.btoa(binary);
};

const base64ToBytes = (value: string) => {
  const binary = window.atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
};

const encodeRememberedPassword = (password: string) => {
  if (!password) return '';

  const passwordBytes = new TextEncoder().encode(password);
  const secretBytes = new TextEncoder().encode(REMEMBERED_LOGIN_SECRET);
  const encodedBytes = passwordBytes.map((byte, index) => byte ^ secretBytes[index % secretBytes.length]);
  return bytesToBase64(encodedBytes);
};

const decodeRememberedPassword = (encodedPassword?: string) => {
  if (!encodedPassword) return '';

  const encodedBytes = base64ToBytes(encodedPassword);
  const secretBytes = new TextEncoder().encode(REMEMBERED_LOGIN_SECRET);
  const passwordBytes = encodedBytes.map((byte, index) => byte ^ secretBytes[index % secretBytes.length]);
  return new TextDecoder().decode(passwordBytes);
};

const getRememberedLogin = (): LoginFormData => {
  if (typeof window === 'undefined') {
    return INITIAL_FORM_STATE;
  }

  try {
    const saved = window.localStorage.getItem(REMEMBERED_LOGIN_KEY);
    if (!saved) {
      return INITIAL_FORM_STATE;
    }

    const parsed = JSON.parse(saved);
    const password = parsed.passwordEncrypted
      ? decodeRememberedPassword(parsed.passwordEncrypted)
      : parsed.password || '';

    if (parsed.password && !parsed.passwordEncrypted) {
      window.localStorage.setItem(
        REMEMBERED_LOGIN_KEY,
        JSON.stringify({
          email: parsed.email || '',
          passwordEncrypted: encodeRememberedPassword(parsed.password),
        })
      );
    }

    return {
      email: parsed.email || '',
      password,
      rememberMe: Boolean(parsed.email || password),
    };
  } catch {
    window.localStorage.removeItem(REMEMBERED_LOGIN_KEY);
    return INITIAL_FORM_STATE;
  }
};

const updateRememberedLogin = (formData: LoginFormData) => {
  if (typeof window === 'undefined') {
    return;
  }

  if (formData.rememberMe) {
    window.localStorage.setItem(
      REMEMBERED_LOGIN_KEY,
      JSON.stringify({
        email: formData.email,
        passwordEncrypted: encodeRememberedPassword(formData.password),
      })
    );
    return;
  }

  window.localStorage.removeItem(REMEMBERED_LOGIN_KEY);
};

export const useReduxLoginForm = (): UseReduxLoginFormReturn => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState<LoginFormData>(() => getRememberedLogin());
  
  // OTP states
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [timer, setTimer] = useState<number>(0);
  const [canResend, setCanResend] = useState<boolean>(true);
  const [localError, setLocalError] = useState<string | null>(null);
  const [resendMessage, setResendMessage] = useState<string | null>(null);
  
  // Get auth state from Redux
  const authState = useSelector((state: any) => state.auth);
  const isLoading = authState?.loading || false;
  const error = authState?.error ? { message: authState.error } : localError ? { message: localError } : null;

  // Timer effect for OTP resend
  useEffect(() => {
    if (timer > 0) {
      const intervalId = setInterval(() => {
        setTimer((prevTime) => prevTime - 1);
      }, 1000);
      return () => clearInterval(intervalId);
    } else if (timer === 0 && !canResend) {
      setCanResend(true);
    }
  }, [timer, canResend]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    if (name === 'rememberMe' && !checked) {
      updateRememberedLogin({ ...formData, rememberMe: false });
    }

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (localError) {
      setLocalError(null);
    }
  }, [formData, localError]);

  // Handle sending OTP
  const handleSendOtp = useCallback(async (): Promise<boolean> => {
    if (!formData.email) {
      setLocalError("Please enter your email address");
      return false;
    }
    
    try {
      setIsSendingOtp(true);
      setLocalError(null);
      setResendMessage(null);
      await sendLoginOtp({ email: formData.email });
      setOtpSent(true);
      setTimer(30);
      setCanResend(false);
      return true;
    } catch (err: any) {
      setLocalError(err.message || "Failed to send OTP. Please try again.");
      return false;
    } finally {
      setIsSendingOtp(false);
    }
  }, [formData.email]);

  // Handle resending OTP
  const handleResendOtp = useCallback(async (): Promise<void> => {
    if (!canResend) return;
    setOtp('');
    const sent = await handleSendOtp();
    if (sent) {
      setResendMessage('A new OTP has been sent to your email.');
    }
  }, [canResend, handleSendOtp]);

  const handleCancelOtp = useCallback(() => {
    setOtpSent(false);
    setOtp('');
    setOtpVerified(false);
    setLocalError(null);
    setResendMessage(null);
    setTimer(0);
    setCanResend(true);
    setFormData((prev) => ({ ...prev, password: '' }));
  }, []);

  // Handle OTP verification and login
  const handleVerifyOtp = useCallback(async (): Promise<void> => {
    if (otp.length !== 6) {
      setLocalError("Please enter a valid 6-digit OTP");
      return;
    }

    // OTP verification is mandatory - never skip it, especially in dev mode
    // When STAGE === 'dev', OTP verification must always be performed
    try {
      setIsVerifying(true);
      setLocalError(null);
      setResendMessage(null);
      const res = await verifyLoginOtp({
        email: formData.email,
        code: otp,
      });
      
      if (res?.success) {
        setOtpVerified(true);
        updateRememberedLogin(formData);
        const signInResult = await userSignIn(formData.email.trim(), formData.password.trim());
        await finalizeLogin(signInResult, formData.email, dispatch);
      } else {
        setLocalError("Invalid OTP. Please try again.");
        setOtp('');
      }
    } catch (err: any) {
      setLocalError(err.message || "Verification failed. Please retry.");
      setOtp('');
    } finally {
      setIsVerifying(false);
    }
  }, [otp, formData, dispatch]);

  // Handle initial form submit - validate credentials first, then send OTP
  const handleSubmit = useCallback(async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    
    // Validate email and password are provided
    if (!formData.email || !formData.password) {
      setLocalError("Please enter both email and password");
      return;
    }

    // If OTP is already sent, don't send again
    if (otpSent) {
      return;
    }

    // Skip OTP verification in dev mode for development
    if (STAGE === 'dev') {
      setOtpVerified(true);
      updateRememberedLogin(formData);
      try {
        setIsValidating(true);
        setLocalError(null);
        const signInResult = await userSignIn(formData.email.trim(), formData.password.trim());
        await finalizeLogin(signInResult, formData.email, dispatch);
      } catch (err: any) {
        setLocalError(err.message || 'Login failed. Please try again.');
        logUserLoginFailedAudit(formData.email, err?.message || 'Login failed');
      } finally {
        setIsValidating(false);
      }
      return;
    }

    // Validate credentials BEFORE sending OTP
    try {
      setIsValidating(true);
      setLocalError(null);
      // Attempt to sign in to validate credentials
      const result = await userSignIn(formData.email.trim(), formData.password.trim());
      
      // Check if there's a challenge (like NEW_PASSWORD_REQUIRED)
      if (result && result.challengeName) {
        // Handle challenge - dispatch action to trigger redirect
        setIsValidating(false);
        dispatch(authChallenge(result.challengeName, result));
        return;
      }
      
      // If sign in succeeds without challenge, immediately sign out to prevent auto-login
      // We only wanted to validate, not actually log in yet
      await userSignOut();
      // Credentials are valid, now send OTP
      await handleSendOtp();
    } catch (err: any) {
      // Credentials are invalid, show error
      let errorMessage = "Invalid username or password";
      if (err.message) {
        if (err.message.includes("Incorrect username or password")) {
          errorMessage = "Incorrect username or password";
        } else if (err.message.includes("User does not exist")) {
          errorMessage = "User does not exist";
        } else if (err.message.includes("User is not confirmed")) {
          errorMessage = "Please verify your email address first";
        } else {
          errorMessage = err.message;
        }
      }
      setLocalError(errorMessage);
      logUserLoginFailedAudit(formData.email, errorMessage);
    } finally {
      setIsValidating(false);
    }
  }, [dispatch, formData, otpSent, handleSendOtp]);

  return {
    formData,
    isLoading: isLoading || isSendingOtp || isValidating,
    error,
    handleInputChange,
    handleSubmit,
    otpSent,
    otp,
    setOtp,
    otpVerified,
    setOtpVerified,
    handleSendOtp,
    handleVerifyOtp,
    isVerifying,
    timer,
    canResend,
    handleResendOtp,
    handleCancelOtp,
    resendMessage,
  };
};
